"use client";

import { ArrowLeft, ArrowRight } from "lucide-react";
import Image from "next/image";
import { useRef } from "react";
import { A11y, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { IconBox } from "@/components/icon-box";
import { TransitionLink } from "@/components/transition-link";
import Bg_Section from "~/public/assets/backgrounds/bg-section-bottom.png";
import type { ServicePageContent } from "../../../types/types";
import { useServiceCardsAnimation } from "./use-service-cards-animation";

type ScenariosServicesSectionProps = {
	service: Pick<ServicePageContent, "scenarios" | "iconVariant">;
};

type ScenarioCardProps = {
	item: NonNullable<ServicePageContent["scenarios"]>["items"][number];
	iconVariant?: ServicePageContent["iconVariant"];
};

function ScenarioCard({ item, iconVariant }: ScenarioCardProps) {
	return (
		<div
			data-service-card
			className="flex h-full flex-col bg-gray-1000 p-8 pb-10 md:px-10 md:pt-14 md:pb-24 2xl:px-18 rounded-2xl 2xl:min-h-140"
		>
			{iconVariant === "outline" ? (
				<IconBox data-service-card-media bordered icon={item.icon} />
			) : (
				<Image
					data-service-card-media
					src={item.icon}
					alt=""
					aria-hidden="true"
					className="w-full max-w-19.25"
					width={400}
					height={400}
				/>
			)}

			<h3 className="mt-16 font-medium font-bricolage text-lg md:text-2xl">
				{item.title}
			</h3>

			<p className="description mt-3 md:mt-6 flex-1">{item.description}</p>

			<TransitionLink
				href={item.href}
				className="mt-4 md:mt-8 flex items-center gap-3 font-semibold text-primary-300"
			>
				Falar com a Flowlab
				<ArrowRight className="size-5" strokeWidth={1.5} />
			</TransitionLink>
		</div>
	);
}

export default function ScenariosServicesSection({
	service,
}: ScenariosServicesSectionProps) {
	const scenarios = service.scenarios;
	const sectionRef = useRef<HTMLElement | null>(null);

	useServiceCardsAnimation(sectionRef, {
		preset: "tilt",
	});

	if (!scenarios) return null;

	return (
		<section
			ref={sectionRef}
			className="relative border-t border-gray-300 bg-gray-1100 text-white"
		>
			<Image
				src={Bg_Section}
				alt="Background decorativo"
				className="absolute w-full h-full top-0 pointer-events-none max-md:object-cover scale-y-[-1]"
				priority={false}
				width={1440}
				height={500}
			/>

			<div className="md:container-padding">
				<div className="container relative md:border-x md:border-gray-300/80 px-6 lg:px-18 3xl:px-24! pt-20 lg:pt-36 pb-20 lg:pb-180">
					<span className="hidden md:inline-flex absolute -top-1.75 -left-1.75 h-3 w-3 border border-gray-300 bg-gray-1000" />
					<span className="hidden md:inline-flex absolute -top-1.75 -right-1.75 h-3 w-3 border border-gray-300 bg-gray-1000" />

					<div className="max-w-110">
						<p className="tag">{scenarios.tag}</p>
						<h2 className="title">{scenarios.title}</h2>
					</div>

					<div data-service-cards-animation-trigger className="mt-12 lg:hidden">
						<Swiper
							a11y={{ enabled: true }}
							className="overflow-visible!"
							modules={[Navigation, A11y]}
							navigation={{
								prevEl: ".scenarios-prev",
								nextEl: ".scenarios-next",
							}}
							slidesPerView="auto"
							spaceBetween={20}
						>
							{scenarios.items.map((item) => (
								<SwiperSlide
									className="h-auto! max-w-[92%] sm:max-w-lg"
									key={item.eyebrow}
								>
									<ScenarioCard item={item} iconVariant={service.iconVariant} />
								</SwiperSlide>
							))}
						</Swiper>

						<div className="mt-10 flex items-center gap-5">
							<button
								aria-label="Card anterior"
								className="scenarios-prev cursor-pointer text-white transition-all duration-300 ease-in-out"
								type="button"
							>
								<ArrowLeft
									className="size-9 text-primary-300"
									strokeWidth={1.5}
								/>
							</button>
							<button
								aria-label="Próximo card"
								className="scenarios-next cursor-pointer text-white transition-all duration-300 ease-in-out"
								type="button"
							>
								<ArrowRight
									className="size-9 text-primary-300"
									strokeWidth={1.5}
								/>
							</button>
						</div>
					</div>
				</div>
			</div>

			<div
				data-service-cards-animation-trigger
				className="absolute top-84 hidden w-full gap-5 px-7 lg:grid lg:grid-cols-3"
			>
				{scenarios.items.map((item) => (
					<ScenarioCard
						key={item.eyebrow}
						item={item}
						iconVariant={service.iconVariant}
					/>
				))}
			</div>
		</section>
	);
}
